import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Cap, Level } from "../utils/rbacCatalog";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import "moment/locale/fr";
import { momentLocaleOf } from "../utils/momentLocale";
import {
  HiOutlineDocumentText,
  HiOutlineTrash,
  HiOutlineQuestionMarkCircle,
  HiOutlineEye,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
} from "react-icons/hi";
import { TextInput, Textarea } from "flowbite-react";
import { Spinner } from "../components/atoms/Spinner";
import { CustomButton } from "../components/atoms/CustomButton";
import { StatusBadge } from "../components/atoms/StatusBadge";
import { ModalShell } from "../components/atoms/ModalShell";
import { PlansModal } from "../components/modals/PlansModal";
import useAuth from "../hooks/useAuth";
import { usePermissions, PlanTier } from "../utils/permissions";
import ConfirmModal from "../components/public/ConfirmModal";
import {
  useGetESignatureTemplates,
  useGetESignatureTemplatePreviewUrl,
  usePreviewESignatureTemplate,
  useConfirmESignatureTemplate,
  useCancelESignatureTemplatePreview,
  useDeleteESignatureTemplate,
} from "../hooks/esignature";
import {
  ESignatureTemplate,
  ESignatureTemplatePreview,
} from "../types/ESignatureTypes";
import CreateSigningRequestModal from "../components/modals/CreateSigningRequestModal";
import ESignatureHelp from "../components/pages/esignature/ESignatureHelp";
import ESignatureGettingStarted from "../components/pages/esignature/ESignatureGettingStarted";
import ESignatureCtaBlock from "../components/pages/esignature/ESignatureCtaBlock";
import CollapsibleHowItWorks from "../components/common/CollapsibleHowItWorks";
import FileDropzone from "../components/atoms/FileDropzone";
import { FieldLabel } from "../components/atoms/FieldLabel";
import SenderEmailGateBanner from "../components/atoms/SenderEmailGateBanner";
import { useSenderEmailGate } from "../hooks/useSenderEmailGate";

type ESignatureTab = "templates" | "help";

const ESignature: React.FC = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    moment.locale(momentLocaleOf(i18n.language));
  }, [i18n.language]);

  const { customer_identification, user } = useAuth();
  const entityIdentification = user?.entity_identification || "";
  const permissions = usePermissions(user);
  const hasESignatureAccess = permissions.isAtLeastTier(PlanTier.ENTERPRISE);

  const [searchParams] = useSearchParams();
  const focusedTemplateId = searchParams.get("template");
  const [missingTemplateId, setMissingTemplateId] = useState<string | null>(
    null
  );

  const [activeTab, setActiveTab] = useState<ESignatureTab>("templates");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showSigningRequestModal, setShowSigningRequestModal] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [selectedTemplateForSigning, setSelectedTemplateForSigning] =
    useState<ESignatureTemplate | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const [uploadName, setUploadName] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showUploadErrors, setShowUploadErrors] = useState(false);
  const [templatePreview, setTemplatePreview] =
    useState<ESignatureTemplatePreview | null>(null);

  const { data: templates, isLoading: templatesLoading } =
    useGetESignatureTemplates(
      customer_identification,
      entityIdentification,
      !hasESignatureAccess
    );

  const sortedTemplates = useMemo(() => {
    if (!templates) return templates;
    return [...templates].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [templates]);

  useEffect(() => {
    if (!focusedTemplateId || !sortedTemplates) return;
    const exists = sortedTemplates.some(
      (template: ESignatureTemplate) =>
        template.template_id === focusedTemplateId
    );
    setMissingTemplateId(exists ? null : focusedTemplateId);
    if (!exists) return;
    window.setTimeout(() => {
      document
        .querySelector(`[data-template-id="${focusedTemplateId}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
  }, [focusedTemplateId, sortedTemplates]);

  const senderGate = useSenderEmailGate(
    customer_identification,
    entityIdentification,
    { enabled: hasESignatureAccess }
  );

  const previewTemplateMutation = usePreviewESignatureTemplate();
  const confirmTemplateMutation = useConfirmESignatureTemplate();
  const cancelPreviewMutation = useCancelESignatureTemplatePreview();
  const deleteTemplateMutation = useDeleteESignatureTemplate();
  const templatePreviewUrlMutation = useGetESignatureTemplatePreviewUrl();

  const handlePreviewTemplate = useCallback(() => {
    if (!uploadName.trim() || !selectedFile) {
      setShowUploadErrors(true);
      return;
    }
    setShowUploadErrors(false);

    const formData = new FormData();
    formData.append("file", selectedFile);

    previewTemplateMutation.mutate(
      {
        customer_identification,
        entityIdentification,
        formData,
      },
      {
        onSuccess: (preview) => {
          setTemplatePreview(preview);
          setShowUploadModal(false);
          setShowPreviewModal(true);
        },
      }
    );
  }, [
    selectedFile,
    uploadName,
    customer_identification,
    entityIdentification,
    previewTemplateMutation,
  ]);

  const handleConfirmTemplate = useCallback(() => {
    if (!templatePreview || !uploadName.trim()) return;

    confirmTemplateMutation.mutate(
      {
        customer_identification,
        entityIdentification,
        data: {
          cdn_file_name: templatePreview.cdn_file_name,
          template_name: uploadName.trim(),
          description: uploadDescription.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          setShowPreviewModal(false);
          setTemplatePreview(null);
          setSelectedFile(null);
          setUploadName("");
          setUploadDescription("");
        },
      }
    );
  }, [
    templatePreview,
    uploadName,
    uploadDescription,
    customer_identification,
    entityIdentification,
    confirmTemplateMutation,
  ]);

  const handleCancelPreview = useCallback(() => {
    if (!templatePreview) {
      setShowPreviewModal(false);
      return;
    }

    cancelPreviewMutation.mutate(
      {
        customer_identification,
        entityIdentification,
        cdn_file_name: templatePreview.cdn_file_name,
      },
      {
        onSuccess: () => {
          setShowPreviewModal(false);
          setTemplatePreview(null);
          setSelectedFile(null);
          setUploadName("");
          setUploadDescription("");
        },
      }
    );
  }, [
    templatePreview,
    customer_identification,
    entityIdentification,
    cancelPreviewMutation,
  ]);

  const handleReupload = useCallback(() => {
    if (templatePreview) {
      cancelPreviewMutation.mutate({
        customer_identification,
        entityIdentification,
        cdn_file_name: templatePreview.cdn_file_name,
      });
    }
    setShowPreviewModal(false);
    setTemplatePreview(null);
    setSelectedFile(null);
    setShowUploadModal(true);
  }, [
    templatePreview,
    customer_identification,
    entityIdentification,
    cancelPreviewMutation,
  ]);

  const handleDeleteTemplateClick = (templateId: string) => {
    setTemplateToDelete(templateId);
    setShowDeleteConfirmModal(true);
  };

  const handlePreviewTemplateFile = (templateId: string) => {
    const tab = window.open("", "_blank");
    templatePreviewUrlMutation.mutate(
      { customer_identification, entityIdentification, templateId },
      {
        onSuccess: (data) => {
          if (tab && data?.signed_url) {
            tab.location.href = data.signed_url;
          } else {
            tab?.close();
          }
        },
        onError: () => {
          tab?.close();
        },
      }
    );
  };

  const handleDeleteTemplate = () => {
    if (!templateToDelete) return;

    deleteTemplateMutation.mutate(
      {
        customer_identification,
        entityIdentification,
        templateId: templateToDelete,
      },
      {
        onSuccess: () => {
          setShowDeleteConfirmModal(false);
          setTemplateToDelete(null);
        },
      }
    );
  };

  const handleSendForSignature = (template: ESignatureTemplate) => {
    if (!senderGate.canSend) {
      const msg =
        senderGate.reason === "default"
          ? t(
              "dashboard.entities.emailSenderValidation.defaultDomainBlockMessage"
            )
          : t(
              "dashboard.entities.emailSenderValidation.unverifiedBlockMessage"
            );
      toast.error(msg as string);
      return;
    }
    setSelectedTemplateForSigning(template);
    setShowSigningRequestModal(true);
  };

  const formatDateTime = (dateStr: string) => moment(dateStr).format("lll");

  return (
    <div className="esignature-page">
      <Helmet>
        <title>{t("esignature.pageTitle")} - Super User Manager</title>
      </Helmet>

      <div className="container mx-auto py-6 px-4">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900">
              {t("esignature.pageTitle")}
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              {t("esignature.pageDescription")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === "templates" &&
              hasESignatureAccess &&
              !templatesLoading &&
              !!templates?.length &&
              permissions.can(Cap.ESIGNATURE_CONFIG, Level.WRITE) && (
                <CustomButton
                  title={t("esignature.templates.uploadButton")}
                  onClick={() => setShowUploadModal(true)}
                  variant="primary"
                  icon="upload"
                />
              )}
          </div>
        </div>

        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex gap-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab("templates")}
              className={`flex items-center gap-2 whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
                activeTab === "templates"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <HiOutlineDocumentText size={18} />
              {t("esignature.tabs.templates")}
            </button>
            <button
              onClick={() => setActiveTab("help")}
              className={`flex items-center gap-2 whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
                activeTab === "help"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <HiOutlineQuestionMarkCircle size={18} />
              {t("esignature.tabs.help")}
            </button>
          </nav>
        </div>

        {activeTab === "help" && <ESignatureHelp />}

        {activeTab === "templates" && !hasESignatureAccess && (
          <>
            <div className="mb-6">
              <ESignatureGettingStarted />
            </div>
            <ESignatureCtaBlock
              variant="upgrade"
              onPrimaryCta={() => setShowPlansModal(true)}
              onHelpClick={() => setActiveTab("help")}
            />
          </>
        )}

        {activeTab === "templates" && hasESignatureAccess && (
          <div className="mt-6">
            {!senderGate.canSend && !senderGate.isLoading && (
              <SenderEmailGateBanner gate={senderGate} className="mb-4" />
            )}

            {missingTemplateId && !templatesLoading && (
              <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                {t("esignature.templates.missingTemplate", {
                  template: missingTemplateId,
                })}
              </div>
            )}

            {templatesLoading ? (
              <div className="flex items-center justify-center h-64">
                <Spinner size="xl" />
              </div>
            ) : !templates || templates.length === 0 ? (
              <>
                <div className="mb-6">
                  <ESignatureGettingStarted />
                </div>
                {permissions.can(Cap.ESIGNATURE_CONFIG, Level.WRITE) && (
                  <ESignatureCtaBlock
                    variant="upload"
                    onPrimaryCta={() => setShowUploadModal(true)}
                    onHelpClick={() => setActiveTab("help")}
                  />
                )}
              </>
            ) : (
              <>
                <CollapsibleHowItWorks
                  collapsible={true}
                  title={t("esignature.howItWorksToggle")}
                  icon={
                    <HiOutlineDocumentText
                      className="text-blue-600"
                      size={18}
                    />
                  }
                  className="mb-4"
                >
                  <ESignatureGettingStarted />
                </CollapsibleHowItWorks>

                <div className="flex flex-col gap-3 md:hidden">
                  {sortedTemplates!.map((template: ESignatureTemplate) => (
                    <div
                      key={template.template_id}
                      data-testid="esignature-template-row"
                      data-template-id={template.template_id}
                      className={`bg-white rounded-lg p-4 border ${
                        template.template_id === focusedTemplateId
                          ? "border-blue-600"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-gray-900 break-words">
                            {template.name}
                          </div>
                          {template.description && (
                            <p className="mt-0.5 text-xs text-gray-500 break-words">
                              {template.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <CustomButton
                            variant="none"
                            size="icon"
                            onClick={() =>
                              handlePreviewTemplateFile(template.template_id)
                            }
                            className="rounded-md text-gray-400 transition-colors enabled:hover:text-blue-600"
                            tooltip={
                              t("esignature.templates.table.preview") as string
                            }
                            isLoading={
                              templatePreviewUrlMutation.isLoading &&
                              templatePreviewUrlMutation.variables
                                ?.templateId === template.template_id
                            }
                            disabled={
                              templatePreviewUrlMutation.isLoading &&
                              templatePreviewUrlMutation.variables
                                ?.templateId === template.template_id
                            }
                            icon={<HiOutlineEye size={18} />}
                          />
                          {permissions.can(
                            Cap.ESIGNATURE_CONFIG,
                            Level.DELETE
                          ) && (
                            <CustomButton
                              variant="none"
                              size="icon"
                              onClick={() =>
                                handleDeleteTemplateClick(template.template_id)
                              }
                              className="rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                              tooltip={t("common.delete") as string}
                              data-testid="esignature-template-delete"
                              icon={<HiOutlineTrash size={18} />}
                            />
                          )}
                        </div>
                      </div>

                      <dl className="mt-3 grid grid-cols-[7.5rem_1fr] items-center gap-x-3 gap-y-1.5 text-xs">
                        <dt className="text-gray-500">
                          {t("esignature.templates.table.templateId")}
                        </dt>
                        <dd className="min-w-0 truncate font-mono text-gray-700">
                          {template.template_id}
                        </dd>
                        <dt className="text-gray-500">
                          {t("esignature.templates.table.createdAt")}
                        </dt>
                        <dd className="text-gray-700">
                          {formatDateTime(template.created_at)}
                        </dd>
                      </dl>

                      {permissions.can(Cap.ESIGNATURE_CONFIG, Level.WRITE) && (
                        <div className="mt-3 flex justify-end">
                          <CustomButton
                            title={t(
                              "esignature.templates.table.sendForSignature"
                            )}
                            onClick={() => handleSendForSignature(template)}
                            variant="secondary"
                            size="sm"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 bg-white">
                  <table className="w-full text-sm text-left text-gray-500">
                    <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-6 py-3 font-medium">
                          {t("esignature.templates.table.name")}
                        </th>
                        <th className="px-6 py-3 font-medium">
                          {t("esignature.templates.table.templateId")}
                        </th>
                        <th className="px-6 py-3 text-right font-medium">
                          {t("esignature.templates.table.actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sortedTemplates!.map((template: ESignatureTemplate) => (
                        <tr
                          key={template.template_id}
                          data-testid="esignature-template-row"
                          data-template-id={template.template_id}
                          className={
                            template.template_id === focusedTemplateId
                              ? "bg-blue-50"
                              : "hover:bg-gray-50"
                          }
                        >
                          <td className="px-6 py-4">
                            <div className="text-sm font-semibold text-gray-900">
                              {template.name}
                            </div>
                            {template.description && (
                              <p className="mt-0.5 max-w-md text-xs text-gray-600">
                                {template.description}
                              </p>
                            )}
                            <div className="mt-0.5 text-xs text-gray-500">
                              {formatDateTime(template.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-mono text-xs text-gray-500">
                              {template.template_id}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end gap-2">
                              {permissions.can(
                                Cap.ESIGNATURE_CONFIG,
                                Level.WRITE
                              ) && (
                                <CustomButton
                                  title={t(
                                    "esignature.templates.table.sendForSignature"
                                  )}
                                  onClick={() =>
                                    handleSendForSignature(template)
                                  }
                                  variant="secondary"
                                  size="sm"
                                />
                              )}
                              <CustomButton
                                variant="none"
                                size="icon"
                                onClick={() =>
                                  handlePreviewTemplateFile(
                                    template.template_id
                                  )
                                }
                                className="rounded-md text-gray-400 transition-colors enabled:hover:text-blue-600"
                                tooltip={
                                  t(
                                    "esignature.templates.table.preview"
                                  ) as string
                                }
                                isLoading={
                                  templatePreviewUrlMutation.isLoading &&
                                  templatePreviewUrlMutation.variables
                                    ?.templateId === template.template_id
                                }
                                disabled={
                                  templatePreviewUrlMutation.isLoading &&
                                  templatePreviewUrlMutation.variables
                                    ?.templateId === template.template_id
                                }
                                icon={<HiOutlineEye size={18} />}
                              />
                              {permissions.can(
                                Cap.ESIGNATURE_CONFIG,
                                Level.DELETE
                              ) && (
                                <CustomButton
                                  variant="none"
                                  size="icon"
                                  onClick={() =>
                                    handleDeleteTemplateClick(
                                      template.template_id
                                    )
                                  }
                                  className="rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                                  tooltip={t("common.delete") as string}
                                  data-testid="esignature-template-delete"
                                  icon={<HiOutlineTrash size={18} />}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <PlansModal
        showPlansModal={showPlansModal}
        setShowPlansModal={setShowPlansModal}
        targetPlan={PlanTier.ENTERPRISE}
      />

      <ModalShell
        show={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setSelectedFile(null);
          setUploadName("");
          setUploadDescription("");
          setShowUploadErrors(false);
        }}
        size="lg"
        title={t("esignature.templates.uploadModal.title")}
        footer={
          <>
            <CustomButton
              title={t("common.cancel")}
              onClick={() => {
                setShowUploadModal(false);
                setSelectedFile(null);
                setUploadName("");
                setUploadDescription("");
                setShowUploadErrors(false);
              }}
              variant="secondary"
            />
            <CustomButton
              title={t("esignature.templates.uploadModal.previewButton")}
              onClick={handlePreviewTemplate}
              variant="primary"
              icon="upload"
              isLoading={previewTemplateMutation.isLoading}
              disabled={
                previewTemplateMutation.isLoading ||
                !permissions.can(Cap.ESIGNATURE_CONFIG, Level.WRITE)
              }
            />
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div>
            <FieldLabel>
              {t("esignature.templates.uploadModal.nameLabel")}
            </FieldLabel>
            <TextInput
              type="text"
              value={uploadName}
              maxLength={60}
              onChange={(e) => {
                setUploadName(e.target.value);
                if (showUploadErrors && e.target.value.trim())
                  setShowUploadErrors(false);
              }}
              color={
                showUploadErrors && !uploadName.trim() ? "failure" : undefined
              }
              className="w-full"
              placeholder={
                t("esignature.templates.uploadModal.namePlaceholder") as string
              }
            />
            <div className="flex items-center justify-between mt-1">
              {showUploadErrors && !uploadName.trim() ? (
                <p className="text-xs text-red-600">
                  {t("esignature.templates.uploadModal.nameRequired")}
                </p>
              ) : (
                <span />
              )}
              <p className="text-xs text-gray-400">{uploadName.length}/60</p>
            </div>
          </div>
          <div>
            <FieldLabel>
              {t("esignature.templates.uploadModal.descriptionLabel")}
            </FieldLabel>
            <Textarea
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              rows={3}
              maxLength={200}
              className="w-full"
              placeholder={
                t(
                  "esignature.templates.uploadModal.descriptionPlaceholder"
                ) as string
              }
            />
            <p className="text-xs text-gray-400 mt-1 text-right">
              {uploadDescription.length}/200
            </p>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              {t("esignature.templates.uploadModal.fileLabel")}
            </label>
            <FileDropzone
              selectedFile={selectedFile}
              onFileSelected={(file) => {
                setSelectedFile(file);
                if (showUploadErrors && file) setShowUploadErrors(false);
              }}
              onFileRemoved={() => setSelectedFile(null)}
              accept={{ "application/pdf": [".pdf"] }}
              hasError={showUploadErrors && !selectedFile}
              errorMessage={
                t("esignature.templates.uploadModal.fileRequired") as string
              }
              hint={t("esignature.templates.uploadModal.fileHint") as string}
            />
          </div>
        </div>
      </ModalShell>

      <ModalShell
        show={showPreviewModal}
        onClose={handleCancelPreview}
        size="lg"
        title={t("esignature.templates.previewModal.title")}
        footer={
          <>
            <CustomButton
              title={t("common.cancel")}
              onClick={handleCancelPreview}
              variant="secondary"
              isLoading={cancelPreviewMutation.isLoading}
            />
            <CustomButton
              title={t("esignature.templates.previewModal.reupload")}
              onClick={handleReupload}
              variant="secondary"
              isLoading={cancelPreviewMutation.isLoading}
              disabled={!permissions.can(Cap.ESIGNATURE_CONFIG, Level.WRITE)}
            />
            <CustomButton
              title={t("esignature.templates.previewModal.confirm")}
              onClick={handleConfirmTemplate}
              variant="primary"
              isLoading={confirmTemplateMutation.isLoading}
              disabled={
                !uploadName.trim() ||
                confirmTemplateMutation.isLoading ||
                !permissions.can(Cap.ESIGNATURE_CONFIG, Level.WRITE)
              }
            />
          </>
        }
      >
        {templatePreview && (
          <div className="flex flex-col gap-5">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {t("esignature.templates.previewModal.templateTitleLabel")}
              </p>
              <h3 className="mt-1 text-lg font-semibold text-gray-900 break-words">
                {uploadName.trim() ||
                  t("esignature.templates.previewModal.untitled")}
              </h3>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-900">
                {t("esignature.templates.previewModal.detectedFieldsTitle")}
              </h4>
              <p className="mt-1 text-sm text-gray-500">
                {templatePreview.placeholders.length === 0
                  ? t(
                      "esignature.templates.previewModal.detectedFieldsEmptyCaption"
                    )
                  : t(
                      "esignature.templates.previewModal.detectedFieldsCaption"
                    ).replace(
                      "{{count}}",
                      String(templatePreview.placeholders.length)
                    )}
              </p>

              {templatePreview.placeholders.length === 0 ? (
                <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700">
                    {t("esignature.templates.previewModal.noPlaceholders")}
                  </p>
                </div>
              ) : (
                <ul className="mt-3 rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                  {templatePreview.placeholders.map((placeholder, index) => {
                    const INPUT_FIELD_MAX_LENGTH = 80;
                    const isInputField = placeholder.category === "input";
                    const fieldName = isInputField
                      ? placeholder.variable.replace("input.", "")
                      : "";
                    const isFieldNameTooLong =
                      isInputField && fieldName.length > INPUT_FIELD_MAX_LENGTH;
                    const isUnknown = placeholder.category === "unknown";

                    return (
                      <li
                        key={index}
                        className="flex flex-col gap-2 px-4 py-3 hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-mono text-xs break-all text-gray-900">{`{{${placeholder.variable}}}`}</div>
                          {isFieldNameTooLong && (
                            <p className="mt-1 text-xs text-amber-600">
                              ⚠{" "}
                              {t(
                                "esignature.templates.previewModal.inputFieldTooLong"
                              )
                                .replace("{{length}}", String(fieldName.length))
                                .replace(
                                  "{{max}}",
                                  String(INPUT_FIELD_MAX_LENGTH)
                                )}
                            </p>
                          )}
                        </div>
                        <div className="shrink-0" title={placeholder.category}>
                          <StatusBadge color="gray" shape="full">
                            <span className="inline-flex items-center gap-1">
                              {isUnknown ? (
                                <HiOutlineXCircle
                                  className="text-red-500"
                                  size={14}
                                  aria-hidden="true"
                                />
                              ) : (
                                <HiOutlineCheckCircle
                                  className="text-green-500"
                                  size={14}
                                  aria-hidden="true"
                                />
                              )}
                              {t(
                                `esignature.templates.previewModal.categories.${placeholder.category}`,
                                {
                                  defaultValue: t(
                                    "esignature.templates.previewModal.categories.unknown"
                                  ),
                                }
                              )}
                            </span>
                          </StatusBadge>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}
      </ModalShell>

      <ConfirmModal
        show={
          showDeleteConfirmModal &&
          permissions.can(Cap.ESIGNATURE_CONFIG, Level.DELETE)
        }
        onClose={() => setShowDeleteConfirmModal(false)}
        title={t("esignature.templates.deleteModal.title")}
        description={
          <div className="space-y-2">
            <p>{t("esignature.templates.deleteModal.description")}</p>
            <p className="text-red-600 font-medium">
              {t("esignature.templates.deleteModal.warning")}
            </p>
          </div>
        }
        cancelBtnName={t("common.cancel")}
        cancelBtnFn={() => {
          setShowDeleteConfirmModal(false);
          setTemplateToDelete(null);
        }}
        confirmBtnName={t("esignature.templates.deleteModal.confirmButton")}
        confirmBtnFn={handleDeleteTemplate}
        ConfirmBtnColor="red"
        isLoading={deleteTemplateMutation.isLoading}
        icon={<HiOutlineTrash size={24} className="text-red-500" />}
        iconBgColor="bg-red-100"
        iconColor="text-red-500"
      />

      <CreateSigningRequestModal
        show={showSigningRequestModal}
        onClose={() => {
          setShowSigningRequestModal(false);
          setSelectedTemplateForSigning(null);
        }}
        templates={templates || []}
        preselectedTemplate={selectedTemplateForSigning}
      />
    </div>
  );
};

export default ESignature;
